import { useEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import { deleteTesterCard, getTesterCard } from '../lib/api.js';
import {
  EXPERIENCE_LABEL,
  FEEDBACK_LABEL,
  STATUS_LABEL,
  TESTER_FORM_URL,
  TIME_LABEL,
  answerLabel,
  cardApps,
  cardStatus,
  deviceName,
  deviceSystem,
  type AppStatus,
  type DeviceType,
  type TesterCardResponse,
} from '../lib/testers.js';
import { SITE_URL } from '../lib/site.js';
import { Modal } from '../components/Modal.js';
import { Note, Problem, button } from '../components/form.js';

/**
 * The tester card, at the bottom of Profile (platform#56).
 *
 * ── THE SAME CARD AS ON RIDGEWILL.COM (Jon, 2026-10-23) ──────────────
 *
 * First a tab of its own in the mock-up, then a plain summary under the form.
 * After trying it on dev Jon asked for the card itself: the one the site shows
 * beside the form at /testers, with the sky band, the five rows, and Edit and
 * Delete where the site has Send. Centered, since it is a thing on the page
 * rather than another row of the form above it.
 *
 * ── MOST ACCOUNTS HAVE NO CARD, SO THAT IS ONE LINE ─────────────────
 *
 * An invitation, not a panel. Profile is the page everybody lands on, and a
 * box about testing on it would be advertising.
 *
 * ── A FAILED LOAD SAYS SO QUIETLY ───────────────────────────────────
 *
 * Muted rather than the red `Problem`: this is the bottom of somebody's
 * profile, and nothing they came to the page for is broken.
 */

/** Whose card it is, for the card's top: the signed-in account. */
export interface CardOwner {
  name?: string | null;
  email: string;
  image?: string | null;
}

export function TesterCard({ user }: { user: CardOwner }) {
  const [data, setData] = useState<TesterCardResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void getTesterCard()
      .then((res) => {
        // Defaults for the reason Subscriptions has one: `call<T>` trusts the
        // shape, and a missing key must read as "nothing" rather than crash.
        if (!cancelled) setData({ card: res.card ?? null, apps: res.apps ?? [], blockers: res.blockers ?? [] });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Nothing while loading: the section is last on the page, so appearing a
  // moment late moves nothing anybody is reading.
  if (!data && !failed) return null;

  return (
    <section aria-label="Tester card" style={block}>
      {failed ? (
        <Note>Your tester card could not be loaded right now.</Note>
      ) : deleted ? (
        <Note tone="ok">Your tester card is deleted, and you are out of every tester group.</Note>
      ) : !data?.card ? (
        <p style={{ margin: 0, fontSize: '.9rem', color: C.textMid }}>
          Want to help Ridgewill by testing new and existing apps?{' '}
          <a href={TESTER_FORM_URL} style={{ color: C.brand }}>
            Make a tester card
          </a>
        </p>
      ) : (
        <Card data={data} user={user} onDelete={() => setConfirming(true)} />
      )}

      <DeleteDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onDeleted={() => {
          setConfirming(false);
          setDeleted(true);
        }}
      />
    </section>
  );
}

function Card({ data, user, onDelete }: { data: TesterCardResponse; user: CardOwner; onDelete: () => void }) {
  const card = data.card!;
  const status = cardStatus(card);
  const all = card.apps.includes('all');
  const apps = cardApps(card, data.apps);
  const answers: [string, string | null][] = [
    ['Experience', answerLabel(EXPERIENCE_LABEL, card.answers.experience)],
    ['Feedback', answerLabel(FEEDBACK_LABEL, card.answers.feedback)],
    ['Time', answerLabel(TIME_LABEL, card.answers.time)],
  ];
  const name = user.name?.trim() || user.email;

  return (
    <article style={cardBox} aria-labelledby="tester-card-heading">
      <header style={{ ...top, backgroundImage: `${TOP_SHADE}, url(${skyUrl()})` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
          <svg width="18" height="18" viewBox="0 0 32 32" aria-hidden="true">
            <path d="M3 26 L11 11 L19 26 Z" fill="#40E0D0" />
            <path d="M13 26 L22 8 L31 26 Z" fill="#fff" />
          </svg>
          <h3 id="tester-card-heading" style={brandText}>
            Tester card
          </h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginTop: '1rem' }}>
          {user.image ? (
            <img src={user.image} alt="" width={44} height={44} style={{ ...avatar, objectFit: 'cover' }} />
          ) : (
            <span style={avatar} aria-hidden="true">
              {name.charAt(0).toUpperCase()}
            </span>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '1.15rem', fontWeight: 800, overflowWrap: 'anywhere' }}>{name}</div>
            <div style={{ fontSize: '.85rem', opacity: 0.92, overflowWrap: 'anywhere' }}>{user.email}</div>
          </div>
        </div>
      </header>

      <dl style={{ margin: 0, padding: '.25rem 1.35rem .5rem' }}>
        <Row label="Apps" first>
          {all ? (
            <AppChip name="All apps" />
          ) : apps.length === 0 ? (
            <Empty>None picked yet</Empty>
          ) : (
            <span style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem' }}>
              {apps.map((app) => (
                <AppChip key={app.slug} name={app.name} status={app.status} />
              ))}
            </span>
          )}
        </Row>

        <Row label="Devices">
          {card.devices.length === 0 ? (
            <Empty>None added yet</Empty>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '.4rem' }}>
              {card.devices.map((d) => (
                <li key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                  <span style={{ color: C.brand, display: 'inline-flex', flex: 'none' }} aria-hidden="true">
                    <DeviceIcon type={d.type} />
                  </span>
                  <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {deviceName(d)} · {deviceSystem(d)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Row>

        {answers.map(([label, value]) => (
          <Row key={label} label={label}>
            {value ?? <Empty>Not answered yet</Empty>}
          </Row>
        ))}
      </dl>

      <footer style={foot}>
        {status.sent ? (
          <div style={{ margin: '0 0 1rem', fontSize: '.88rem', lineHeight: 1.5, color: C.textMid }}>
            <p style={{ ...sentLine, margin: 0 }}>
              <span style={dot} aria-hidden="true" />
              {status.text}
            </p>
            <p style={{ margin: '.15rem 0 0' }}>We have your card. Change anything on ridgewill.com and you can send it again.</p>
          </div>
        ) : (
          /* Only an unsent card lists what is missing. The sentences are the
             server's (`blockers`), so "ready to send" is auth agreeing, not
             this file guessing. */
          <div style={{ margin: '0 0 1rem', fontSize: '.88rem', lineHeight: 1.5 }}>
            <p style={{ margin: 0, fontWeight: 700 }}>Not sent yet</p>
            {data.blockers.length > 0 ? (
              <>
                <p style={{ margin: '.25rem 0 0', color: C.textMid }}>Still needed:</p>
                <ul style={{ margin: '.25rem 0 0', paddingLeft: '1.2rem', color: C.textMid }}>
                  {data.blockers.map((b) => (
                    <li key={`${b.code}:${b.app ?? ''}`}>{b.message}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p style={{ margin: '.25rem 0 0', color: C.textMid }}>Ready to send.</p>
            )}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
          {/* A link, because it leaves for the site: the card is changed there. */}
          <a href={TESTER_FORM_URL} style={status.sent ? linkButton : primaryLink}>
            {status.sent ? 'Edit' : 'Finish my card'}
          </a>
          <button type="button" onClick={onDelete} style={{ ...footButton, color: DANGER, borderColor: DANGER }}>
            Delete card
          </button>
        </div>
      </footer>
    </article>
  );
}

/**
 * One plain confirmation, not a typed phrase like Delete My Account. This is
 * recoverable: a new card can be made at any time. What does need saying,
 * because it is not obvious from the word "card", is the tester groups.
 */
function DeleteDialog({ open, onClose, onDeleted }: { open: boolean; onClose: () => void; onDeleted: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cleared on each open, since the dialog outlives a close to fade out.
  useEffect(() => {
    if (!open) return;
    setBusy(false);
    setError(null);
  }, [open]);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await deleteTesterCard();
      onDeleted();
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : 'Your tester card could not be deleted.');
    }
  }

  return (
    <Modal open={open} title="Delete your tester card?" onClose={onClose}>
      <p style={{ margin: '0 0 .75rem', lineHeight: 1.55 }}>
        This deletes your tester card, with your devices and answers.
      </p>
      <p style={{ margin: '0 0 1.25rem', lineHeight: 1.55 }}>
        It also takes you out of every Ridgewill tester group, so you will not get any more test
        invites. You can make a new card on ridgewill.com at any time.
      </p>
      {error ? <Problem>{error}</Problem> : null}
      <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => void confirm()}
          disabled={busy}
          style={{ ...button, marginTop: 0, color: DANGER, borderColor: DANGER }}
        >
          {busy ? 'Deleting...' : 'Delete card'}
        </button>
        <button type="button" onClick={onClose} disabled={busy} style={{ ...button, marginTop: 0 }}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

function Row({ label, first = false, children }: { label: string; first?: boolean; children: React.ReactNode }) {
  return (
    <div style={first ? { ...row, borderTop: 0 } : row}>
      <dt style={{ color: C.textMid }}>{label}</dt>
      <dd style={{ margin: 0, minWidth: 0, overflowWrap: 'anywhere', fontWeight: 600 }}>{children}</dd>
    </div>
  );
}

/** A gap says so. A blank reads as the answer being empty. */
function Empty({ children }: { children: React.ReactNode }) {
  return <span style={{ color: C.textMid, fontWeight: 400 }}>{children}</span>;
}

/**
 * An app as the site's card shows it: a pill with a ring in the stage's color.
 * The stage itself is read out and shown on hover.
 */
function AppChip({ name, status }: { name: string; status?: AppStatus }) {
  const ring = status === 'in-dev' ? '#c9845a' : status === 'alpha' ? C.brand : C.turquoise;
  const stage = status ? (STATUS_LABEL[status] ?? status) : undefined;
  return (
    <span style={chip} title={stage}>
      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: C.surface, boxShadow: `0 0 0 2px ${ring}` }} />
      {name}
      {stage ? <span style={visuallyHidden}> ({stage})</span> : null}
    </span>
  );
}

/** Inline SVG, as elsewhere here: three outlines do not justify an icon package. */
function DeviceIcon({ type }: { type: DeviceType }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {type === 'phone' && (
        <>
          <rect x="7" y="2.5" width="10" height="19" rx="2" />
          <path d="M11 18.5h2" />
        </>
      )}
      {type === 'tablet' && (
        <>
          <rect x="4.5" y="2.5" width="15" height="19" rx="2" />
          <path d="M11 18.5h2" />
        </>
      )}
      {type === 'computer' && (
        <>
          <rect x="3" y="4" width="18" height="12" rx="1.5" />
          <path d="M8 20h8M12 16v4" />
        </>
      )}
    </svg>
  );
}

/** The site's sky, by the reader's time of day, under the site's blue shade. */
function skyUrl(now: Date = new Date()): string {
  const h = now.getHours();
  const part = h >= 5 && h < 10 ? 'dawn' : h >= 10 && h < 17 ? 'midday' : h >= 17 && h < 20 ? 'sunset' : 'night';
  return `${SITE_URL}/assets/home/sky-${part}${part === 'dawn' ? '.jpg' : '.webp'}`;
}
const TOP_SHADE =
  'linear-gradient(100deg, rgba(18, 48, 127, .94) 0%, rgba(30, 79, 208, .82) 60%, rgba(30, 79, 208, .45) 100%)';

/** The red Delete My Account uses, so "dangerous" is one red on this portal. */
const DANGER = C.dangerText;

const block: React.CSSProperties = {
  marginTop: '2.5rem',
  paddingTop: '1.5rem',
  borderTop: `1px solid ${C.inputBorder}`,
};

const cardBox: React.CSSProperties = {
  maxWidth: 420,
  margin: '0 auto',
  borderRadius: 14,
  overflow: 'hidden',
  background: C.surface,
  color: C.text,
  border: `1px solid ${C.border}`,
  boxShadow: '0 1px 3px rgba(18, 48, 127, .12), 0 18px 50px -18px rgba(18, 48, 127, .4)',
};

const top: React.CSSProperties = {
  color: '#fff',
  padding: '1.2rem 1.35rem 1.1rem',
  backgroundColor: '#26365a',
  backgroundSize: 'cover',
  backgroundPosition: 'center 62%',
};

const brandText: React.CSSProperties = {
  margin: 0,
  fontSize: '.8rem',
  fontWeight: 700,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
};

const avatar: React.CSSProperties = {
  width: 44,
  height: 44,
  flex: 'none',
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  background: C.turquoise,
  color: '#0b2a3a',
  fontWeight: 800,
  fontSize: '1.1rem',
};

const row: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '6rem minmax(0, 1fr)',
  gap: '.75rem',
  alignItems: 'baseline',
  padding: '.7rem 0',
  fontSize: '.88rem',
  borderTop: `1px solid ${C.border}`,
};

const chip: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '.45rem',
  padding: '.2rem .6rem',
  borderRadius: 999,
  border: `1px solid ${C.border}`,
  fontSize: '.8rem',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
};

const foot: React.CSSProperties = {
  padding: '1rem 1.35rem 1.25rem',
  background: C.page,
  borderTop: `1px solid ${C.border}`,
};

const sentLine: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '.5rem',
  fontSize: '.95rem',
  fontWeight: 700,
  color: C.successText,
};

const dot: React.CSSProperties = { width: 8, height: 8, borderRadius: '50%', background: C.successDot };

/** The outline button, filling its half of the footer. */
const footButton: React.CSSProperties = {
  ...button,
  marginTop: 0,
  width: '100%',
  textAlign: 'center',
};

/** A link doing a button's job. The outline button, as an anchor. */
const linkButton: React.CSSProperties = {
  ...footButton,
  display: 'inline-block',
  boxSizing: 'border-box',
  textDecoration: 'none',
};

/** Support's filled primary, as an anchor, so "the next step" looks the same everywhere. */
const primaryLink: React.CSSProperties = {
  ...linkButton,
  background: C.brand,
  color: '#fff',
  borderColor: C.brand,
};
